<template>
  <div :class="classResolver(classes)" class="dc-content" v-html="content"></div>
</template>

<script setup>
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import classResolver from '@apexl/icarus-core/src/services/classResolver';

// Icarus counterpart of `apexl\Io\modules\dynamicComponents\Component\HtmlContent`
// (registry NAME `DcContent`) — see that class's docblock. Renders whatever
// pre-baked HTML server-rendered types (ListType, QuickLinksType, etc.) hand
// down as the `content` prop. No fetch, no client logic — a pure v-html
// sink, deliberately not the vendor `Content.vue` (expects a `components`
// array) or `ContentHtml.vue` (reads `this.props.content`, incompatible
// with how `DynamicComponents.vue` spreads props).
//
// Card chrome (border/shadow/radius/header) is styled purely from the
// generic markup `ListType::render()` already emits (`dc-list__header` /
// `dc-list-item` / `dc-list-secondary`, etc.) — no vendor PHP change, so
// this stays correct for any current or future `list` tile, not just the
// module roster.
//
// `design/components/module-roster-list.html`'s status dot + pill isn't a
// dedicated markup feature of `ListType` — it only ever emits a per-row
// `icon_field` glyph (`dc-list-icon dc-list-icon--{tone}` wrapping a real
// FA `<i>`) and a plain `dc-list-secondary` text line. The module roster
// tile config (`DashboardHomeAlterHook::DEFAULT_TILES`, mirrored in
// `AddModuleRosterStatusIconToDaedalusWorkspaces`) sets `icon_field =>
// 'status'`, so the tone class is exactly 'live'/'partial'/'planned' (no
// real icon behind it) — the CSS below repurposes that span as the design's
// status dot (hiding the FA glyph) and uses a `:has()` selector on the same
// tone class to pill-colour the sibling `status_label` text, all without
// any vendor PHP change. Scoped to those three exact tone names so a
// future list using `icon_field` for a real FA glyph (e.g. call=blue,
// email=indigo, per `ListType`'s own docblock) is unaffected.
defineProps({
  ...commonProps,
  content: {
    type: String,
    default: '',
  },
});
</script>

<style lang="scss">
.dc-content {
  height: 100%;
  background: var(--theme-bg-card);
  border: 1px solid var(--theme-border);
  border-radius: var(--theme-radius-lg, 10px);
  box-shadow: var(--theme-shadow-card);
  overflow: hidden;

  .dc-list {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .dc-list__header {
    padding: 14px 16px 10px;
    border-bottom: 1px solid var(--theme-border-soft);
  }

  .dc-list-heading {
    margin: 0;
    font-size: 0.9rem;
    color: var(--theme-text-primary);
  }

  .dc-list__body {
    padding: 4px 8px;
    overflow-y: auto;
  }

  .dc-list-items {
    display: flex;
    flex-direction: column;
  }

  .dc-list-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 8px;
    border-bottom: 1px solid var(--theme-border-soft);

    &:last-child {
      border-bottom: none;
    }

    // Module-roster build-status pill — see this file's top docblock.
    // Colour keyed off the sibling status-dot's tone class via `:has()`
    // so the plain `status_label` text becomes a coloured pill without any
    // vendor markup change.
    &:has(.dc-list-icon--live) .dc-list-secondary,
    &:has(.dc-list-icon--partial) .dc-list-secondary,
    &:has(.dc-list-icon--planned) .dc-list-secondary {
      display: inline-block;
      font-size: 0.66rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      padding: 3px 8px;
      border-radius: 3px;
    }

    &:has(.dc-list-icon--live) .dc-list-secondary {
      background: #e1f3ea;
      color: #1c7a4d;
    }

    &:has(.dc-list-icon--partial) .dc-list-secondary {
      background: #fbeeda;
      color: #9c6a10;
    }

    &:has(.dc-list-icon--planned) .dc-list-secondary {
      background: var(--theme-border-soft);
      color: var(--theme-text-tertiary);
    }
  }

  .dc-list-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border-radius: var(--theme-radius, 6px);
    color: var(--theme-text-tertiary);
    font-size: 0.8rem;

    // Module-roster build-status tones — see this file's top docblock.
    // These three tone names are status dots, not FA glyphs: hide the
    // (invalid) icon glyph and render the span itself as a small circle.
    &--live,
    &--partial,
    &--planned {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--theme-border-strong);

      i {
        display: none;
      }
    }

    &--live {
      background: var(--theme-status-success);
    }

    &--partial {
      background: var(--theme-status-warning);
    }
  }

  .dc-list-body {
    flex: 1;
    min-width: 0;
  }

  .dc-list-primary {
    color: var(--theme-text-primary);
    font-size: 0.83rem;
    font-weight: 500;
  }

  .dc-list-secondary {
    color: var(--theme-text-secondary);
    font-size: 0.72rem;
  }

  .dc-list-stamp {
    flex-shrink: 0;
    color: var(--theme-text-tertiary);
    font-size: 0.68rem;
    font-family: var(--theme-font-mono);
    white-space: nowrap;
  }

  .dc-welcome-fallback {
    padding: 16px;
    text-align: center;
  }

  .dc-quick-links__title {
    margin: 0 0 8px;
    color: var(--theme-text-primary);
  }

  .dc-quick-links__grid {
    display: grid;
    grid-template-columns: repeat(var(--dc-cols, 3), 1fr);
    gap: 12px;
  }

  .dc-quick-link {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 12px;
    border-radius: var(--theme-radius, 6px);
    border: 1px solid var(--theme-border);
    color: var(--theme-text-primary);
    text-decoration: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: var(--theme-accent-primary);
      box-shadow: var(--theme-shadow-card);
      color: var(--theme-text-primary);
    }
  }

  .dc-quick-link__icon {
    color: var(--theme-accent-primary);
    font-size: 1.1rem;
  }

  .dc-quick-link__label {
    font-weight: 500;
  }

  .dc-quick-link__description {
    color: var(--theme-text-secondary);
    font-size: 0.8rem;
  }

  .dc-quick-links__empty {
    padding: 8px 0;
  }
}
</style>
